import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trophy, Lightning, Leaf, Fire, Star, Crown, Lock } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Gamification() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem("blumove_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const [meRes, boardRes] = await Promise.all([
          token ? axios.get(`${API}/gamification/me`, { headers }) : Promise.resolve({ data: null }),
          axios.get(`${API}/gamification/leaderboard`),
        ]);
        setData(meRes.data);
        setBoard(boardRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="pt-20 px-6 text-center"><p style={{ color: "var(--text-muted)" }}>{t("gamification.loading")}</p></div>;

  const stats = data?.stats || { shipments_sent: 0, co2_saved: 0, streak: 0, level: 1, xp: 0, badges_unlocked: [], ratings_given: 0 };
  const allBadges = data?.all_badges || [];
  const xpProg = Math.min(100, Math.round((stats.xp % 200) / 200 * 100));

  return (
    <div data-testid="gamification-page" className="pt-20 px-6 pb-12">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 style={{ fontFamily: "Outfit", fontWeight: 800, fontSize: "2.25rem", marginBottom: 6 }}>
            <Trophy size={28} weight="duotone" style={{ verticalAlign: "-4px", marginRight: 8, color: "#f59e0b" }} />
            {t("gamification.title")}
          </h1>
          <p style={{ color: "var(--text-muted)" }}>{t("gamification.subtitle")}</p>
        </div>

        {/* Top stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Lightning} color="#f59e0b" label={t("gamification.level")} value={stats.level} sub={`${stats.xp} ${t("gamification.xp")}`} testid="stat-level" />
          <StatCard icon={Leaf} color="#10b981" label={t("gamification.co2_saved")} value={`${stats.co2_saved} kg`} sub={t("gamification.shipments_count", { count: stats.shipments_sent })} testid="stat-co2" />
          <StatCard icon={Fire} color="#ef4444" label={t("gamification.streak")} value={`${stats.streak}d`} sub={t("gamification.streak_sub")} testid="stat-streak" />
          <StatCard icon={Star} color="#a78bfa" label={t("gamification.ratings_given")} value={stats.ratings_given} sub={t("gamification.ratings_sub")} testid="stat-ratings" />
        </div>

        {/* XP progress to next level */}
        <div className="bento-item p-5 mb-10" data-testid="xp-progress">
          <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>{t("gamification.next_level", { level: stats.level + 1 })}</span>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: "0.78rem", color: "var(--text-muted)" }}>{stats.xp % 200}/200 {t("gamification.xp")}</span>
          </div>
          <div style={{ height: 10, borderRadius: 999, background: "var(--bg)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${xpProg}%`, background: "linear-gradient(90deg, #f59e0b, #ef4444)", transition: "width .6s" }} />
          </div>
        </div>

        {/* Badges grid */}
        <div className="mb-10">
          <h2 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.2rem", marginBottom: 14 }}>{t("gamification.badges_title")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="badges-grid">
            {allBadges.map(b => {
              const unlocked = stats.badges_unlocked.includes(b.id);
              const localizedName = t(`badges.${b.id}`, { defaultValue: b.name });
              return (
                <div
                  key={b.id}
                  data-testid={`badge-${b.id}`}
                  className={unlocked ? "" : "opacity-50"}
                  style={{
                    padding: 16, borderRadius: 14,
                    background: unlocked ? "linear-gradient(180deg, rgba(245,158,11,0.12), rgba(245,158,11,0))" : "var(--surface)",
                    border: `1px solid ${unlocked ? "rgba(245,158,11,0.3)" : "var(--border-color)"}`,
                    textAlign: "center",
                    transition: "transform .25s",
                  }}
                >
                  <div style={{ fontSize: "2.4rem", lineHeight: 1, marginBottom: 6, filter: unlocked ? "none" : "grayscale(1)" }}>
                    {unlocked ? b.icon : <Lock size={28} style={{ color: "var(--text-muted)" }} />}
                  </div>
                  <div style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "0.85rem" }}>{localizedName}</div>
                  <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", marginTop: 3 }}>+{b.xp} {t("gamification.xp")}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Leaderboard */}
        <div>
          <h2 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.2rem", marginBottom: 14 }}>
            <Crown size={20} weight="duotone" style={{ verticalAlign: "-3px", marginRight: 6, color: "#f59e0b" }} />
            {t("gamification.leaderboard_title")}
          </h2>
          <div className="bento-item overflow-hidden" data-testid="leaderboard">
            {(!board || board.top.length === 0) && (
              <p style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                {t("gamification.empty_state")}
              </p>
            )}
            {board?.top?.map((row, i) => (
              <div key={i} data-testid={`leaderboard-row-${i}`} style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: i < board.top.length - 1 ? "1px solid var(--border-color)" : "none" }}>
                <div style={{ width: 32, fontFamily: "Outfit", fontWeight: 800, fontSize: "1.1rem", color: i === 0 ? "#f59e0b" : i === 1 ? "#94a3b8" : i === 2 ? "#b45309" : "var(--text-muted)" }}>
                  #{i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{row.user_name}</div>
                  <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{t("gamification.level")} {row.level} · {t("gamification.shipments_count", { count: row.shipments_sent })} · {row.badges_unlocked.length} {t("gamification.badges_title").toLowerCase()}</div>
                </div>
                <div style={{ fontFamily: "JetBrains Mono", fontWeight: 700, color: "#10b981" }}>{row.co2_saved} kg</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <div className="bento-item p-5" data-testid={testid} style={{ borderTop: `2px solid ${color}` }}>
      <div className="flex items-center gap-2" style={{ color }}>
        <Icon size={18} weight="duotone" />
        <span style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</span>
      </div>
      <div style={{ fontFamily: "Outfit", fontWeight: 800, fontSize: "1.7rem", marginTop: 6 }}>{value}</div>
      <div style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>{sub}</div>
    </div>
  );
}
